import { api, Vehicle, Shipment, Stats } from '../api';
import { statusBadge, navigate, glassStatCard, getMapTileUrl, isDark } from '@demo/ui';

declare const L: any;

let map: any = null;
let tileLayer: any = null;
let markers: Map<string, any> = new Map();
let pollInterval: number | null = null;
let panelOpen = false;
let routeLayer: any = null;

function markerColor(v: Vehicle): string {
  if (v.status === 'maintenance') return '#ef4444';
  if (v.status === 'idle') return '#eab308';
  return '#22c55e';
}

function markerColorClass(v: Vehicle): string {
  if (v.status === 'maintenance') return 'red';
  if (v.status === 'idle') return 'yellow';
  return 'green';
}

function createTruckIcon(v: Vehicle): any {
  const color = markerColor(v);
  const cls = markerColorClass(v);
  return L.divIcon({
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    html: `
      <div class="relative flex items-center justify-center pulse-dot ${cls}" style="width:32px;height:32px;">
        <div class="absolute w-7 h-7 rounded-full border-2" style="border-color:${color}; background:${color}22;"></div>
        <svg class="relative z-10 w-4 h-4" style="color:${color}" fill="currentColor" viewBox="0 0 20 20">
          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-5a1 1 0 00-.293-.707l-3-3A1 1 0 0016 4H3zm13 2.5V9h3l-2.5-2.5H16z"/>
        </svg>
      </div>
    `,
  });
}

function tooltipClass(): string {
  return isDark()
    ? '!bg-slate-800 !border-slate-600 !text-slate-100 !rounded-lg !px-2 !py-1 !shadow-xl'
    : '!bg-white !border-gray-200 !text-gray-900 !rounded-lg !px-2 !py-1 !shadow-xl';
}

function clearRouteOverlay() {
  if (routeLayer) {
    map?.removeLayer(routeLayer);
    routeLayer = null;
  }
}

async function showRouteOverlay(vehicle: Vehicle, activeShipment: Shipment | undefined) {
  clearRouteOverlay();
  try {
    const route = await api.getVehicleRoute(vehicle.id);
    if (!route.waypoints || route.waypoints.length < 2) return;

    const first = route.waypoints[0];
    const last = route.waypoints[route.waypoints.length - 1];
    if (Math.abs(first.lat - last.lat) < 0.01 && Math.abs(first.lng - last.lng) < 0.01) return;

    routeLayer = L.layerGroup().addTo(map);

    const origin = route.waypoints[0];
    const dest = route.waypoints[route.waypoints.length - 1];
    const latlngs = route.waypoints.map((p: { lat: number; lng: number }) => [p.lat, p.lng]);

    L.polyline(latlngs, {
      color: '#6366f1',
      weight: 3,
      opacity: 0.7,
      dashArray: '8, 8',
    }).addTo(routeLayer);

    const originLabel = activeShipment?.origin || 'Origin';
    const destLabel = activeShipment?.destination || 'Destination';

    L.circleMarker([origin.lat, origin.lng], {
      radius: 8,
      color: '#22c55e',
      fillColor: '#22c55e',
      fillOpacity: 0.9,
      weight: 2,
    }).addTo(routeLayer).bindTooltip(
      `<div class="text-xs font-medium"><span class="text-green-400">Origin</span><br/>${originLabel}</div>`,
      { direction: 'top', offset: [0, -10], className: tooltipClass(), permanent: false }
    );

    L.circleMarker([dest.lat, dest.lng], {
      radius: 8,
      color: '#ef4444',
      fillColor: '#ef4444',
      fillOpacity: 0.9,
      weight: 2,
    }).addTo(routeLayer).bindTooltip(
      `<div class="text-xs font-medium"><span class="text-red-400">Destination</span><br/>${destLabel}</div>`,
      { direction: 'top', offset: [0, -10], className: tooltipClass(), permanent: false }
    );

    const bounds = L.latLngBounds(latlngs);
    const isMobile = window.innerWidth < 640;
    map.fitBounds(bounds, { paddingTopLeft: [30, 30], paddingBottomRight: isMobile ? [30, 30] : [420, 50] });
  } catch (e) {
    console.warn('Failed to show route overlay:', e);
  }
}

async function openVehiclePanel(container: HTMLElement, vehicle: Vehicle) {
  panelOpen = true;
  let panel = container.querySelector('#vehicle-panel') as HTMLElement;
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'vehicle-panel';
    panel.className = 'absolute top-0 right-0 h-full w-full sm:w-96 bg-th-panel backdrop-blur-xl border-l border-th-border z-[1000] overflow-y-auto transform translate-x-full transition-transform duration-300 shadow-2xl';
    container.appendChild(panel);
  }

  let shipments: Shipment[] = [];
  try { shipments = await api.getVehicleShipments(vehicle.id); } catch (e) { console.warn('Failed to load vehicle shipments:', e); }
  const activeShipment = shipments.find(s => s.status === 'in_transit' || s.status === 'loading');

  showRouteOverlay(vehicle, activeShipment);

  const tempDisplay = activeShipment?.current_temp != null
    ? `<span class="${activeShipment.current_temp > activeShipment.temp_max || activeShipment.current_temp < activeShipment.temp_min ? 'text-red-400' : 'text-green-400'}">${activeShipment.current_temp.toFixed(1)}°C</span>`
    : '<span class="text-th-text-3">N/A</span>';

  const humidityDisplay = activeShipment?.current_humidity != null
    ? (() => {
        const outOfRange = activeShipment.humidity_min != null && activeShipment.humidity_max != null &&
          (activeShipment.current_humidity < activeShipment.humidity_min || activeShipment.current_humidity > activeShipment.humidity_max);
        return `<span class="${outOfRange ? 'text-red-400' : 'text-cyan-400'}">${activeShipment.current_humidity.toFixed(0)}%</span>`;
      })()
    : '<span class="text-th-text-3">N/A</span>';

  const isReefer = vehicle.type === 'reefer_truck';

  panel.innerHTML = `
    <div class="p-5">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold">${vehicle.name}</h2>
        <button id="close-panel" class="text-th-text-3 hover:text-th-text transition-colors p-1">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="flex items-center gap-3 mb-6">
        <span class="text-sm text-th-text-3">${vehicle.id}</span>
        ${statusBadge(vehicle.status)}
        <span class="text-xs text-th-text-3">${isReefer ? 'Reefer Truck' : 'Sea Container'}</span>
      </div>

      <div class="grid ${isReefer ? 'grid-cols-3' : 'grid-cols-2'} gap-3 mb-3">
        <div class="bg-th-surface rounded-lg p-3">
          <p class="text-xs text-th-text-3 mb-1">Temperature</p>
          <p class="text-xl font-bold">${tempDisplay}</p>
          ${activeShipment ? `<p class="text-xs text-th-text-3 mt-1">${activeShipment.temp_min}° to ${activeShipment.temp_max}°C</p>` : ''}
        </div>
        ${isReefer ? `
        <div class="bg-th-surface rounded-lg p-3">
          <p class="text-xs text-th-text-3 mb-1">Humidity</p>
          <p class="text-xl font-bold">${humidityDisplay}</p>
          ${activeShipment?.humidity_min != null ? `<p class="text-xs text-th-text-3 mt-1">${activeShipment.humidity_min}% to ${activeShipment.humidity_max}%</p>` : '<p class="text-xs text-th-text-3 mt-1">Not required</p>'}
        </div>
        ` : ''}
        <div class="bg-th-surface rounded-lg p-3">
          <p class="text-xs text-th-text-3 mb-1">Speed</p>
          <p class="text-xl font-bold text-blue-400">${vehicle.speed.toFixed(0)} <span class="text-sm font-normal">km/h</span></p>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-3 mb-6">
        <div class="bg-th-surface rounded-lg p-3">
          <p class="text-xs text-th-text-3 mb-1">Driver</p>
          <p class="text-sm font-medium">${vehicle.current_driver || 'Unassigned'}</p>
        </div>
        <div class="bg-th-surface rounded-lg p-3">
          <p class="text-xs text-th-text-3 mb-1">Home Base</p>
          <p class="text-sm font-medium">${vehicle.home_base}</p>
        </div>
      </div>

      ${activeShipment ? `
        <div class="mb-6">
          <h3 class="text-sm font-semibold text-th-text-3 mb-3 uppercase tracking-wider">Active Shipment</h3>
          <a href="#/shipments/${activeShipment.id}" class="block bg-th-surface rounded-lg p-4 hover:bg-th-surface-hover transition-colors">
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium text-blue-400">${activeShipment.id}</span>
              ${statusBadge(activeShipment.status)}
            </div>
            <p class="text-sm mb-1">${activeShipment.origin} → ${activeShipment.destination}</p>
            <p class="text-xs text-th-text-3">${activeShipment.cargo_type} — ${activeShipment.cargo_description}</p>
          </a>
        </div>
      ` : ''}

      <div class="flex gap-2">
        <a href="#/vehicles/${vehicle.id}" class="flex-1 text-center py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors">View Details</a>
      </div>
    </div>
  `;

  requestAnimationFrame(() => {
    panel.classList.remove('translate-x-full');
    panel.classList.add('translate-x-0');
  });

  panel.querySelector('#close-panel')?.addEventListener('click', () => closePanel(container));
}

function closePanel(container: HTMLElement) {
  panelOpen = false;
  clearRouteOverlay();
  const panel = container.querySelector('#vehicle-panel') as HTMLElement;
  if (panel) {
    panel.classList.remove('translate-x-0');
    panel.classList.add('translate-x-full');
  }
}

function updateTileLayer() {
  if (!map || !tileLayer) return;
  map.removeLayer(tileLayer);
  tileLayer = L.tileLayer(getMapTileUrl(), { maxZoom: 19 }).addTo(map);
}

export async function renderFleetMap(container: HTMLElement): Promise<() => void> {
  const [vehicles, stats] = await Promise.all([api.getVehicles(), api.getStats()]);

  container.innerHTML = `
    <div class="relative h-full" id="map-wrapper">
      <div class="absolute inset-0 z-0"><div id="leaflet-map" style="width:100%;height:100%"></div></div>
      <div class="absolute top-3 left-3 right-3 sm:top-5 sm:left-5 sm:right-auto z-[500] flex gap-2 sm:gap-3 overflow-x-auto">
        ${glassStatCard({ label: 'In Transit', value: String(stats.in_transit), dotColor: 'bg-green-400' })}
        ${glassStatCard({ label: 'Alerts', value: String(stats.active_alerts), dotColor: 'bg-red-400' })}
        ${glassStatCard({ label: 'Avg Temp', value: `${stats.avg_temp.toFixed(1)}<span class="text-sm sm:text-lg text-th-text-3 ml-0.5">°C</span>`, dotColor: 'bg-blue-400' })}
      </div>
      <div class="absolute bottom-3 left-3 sm:bottom-5 sm:left-5 z-[500]">
        <div class="glass rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 flex items-center gap-3 sm:gap-4">
          <div class="flex items-center gap-1.5 sm:gap-2">
            <div class="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-green-400 shadow-sm shadow-green-400/40"></div><span class="text-[10px] sm:text-xs text-th-text-2 font-medium">Active</span>
          </div>
          <div class="flex items-center gap-1.5 sm:gap-2">
            <div class="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-yellow-400 shadow-sm shadow-yellow-400/40"></div><span class="text-[10px] sm:text-xs text-th-text-2 font-medium">Idle</span>
          </div>
          <div class="flex items-center gap-1.5 sm:gap-2">
            <div class="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-red-400 shadow-sm shadow-red-400/40"></div><span class="text-[10px] sm:text-xs text-th-text-2 font-medium">Maint.</span>
          </div>
        </div>
      </div>
    </div>
  `;

  map = L.map('leaflet-map', {
    center: [44.0, 22.0],
    zoom: 5,
    zoomControl: true,
    attributionControl: false,
  });

  tileLayer = L.tileLayer(getMapTileUrl(), { maxZoom: 19 }).addTo(map);

  const wrapper = container.querySelector('#map-wrapper')!;

  vehicles.forEach(v => {
    if (!v.latitude && !v.longitude) return;
    const marker = L.marker([v.latitude, v.longitude], { icon: createTruckIcon(v) });
    marker.addTo(map);
    marker.on('click', async () => {
      const fresh = await api.getVehicle(v.id);
      openVehiclePanel(wrapper as HTMLElement, fresh);
    });
    marker.bindTooltip(`<div class="text-xs font-medium">${v.name}<br/><span class="text-th-text-3">${v.id}</span></div>`, {
      direction: 'top',
      offset: [0, -16],
      className: tooltipClass(),
    });
    markers.set(v.id, marker);
  });

  const onThemeChange = () => updateTileLayer();
  window.addEventListener('themechange', onThemeChange);

  pollInterval = window.setInterval(async () => {
    if (!document.getElementById('leaflet-map')) {
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
      return;
    }
    try {
      const updated = await api.getVehicles();
      updated.forEach(v => {
        const m = markers.get(v.id);
        if (m && v.latitude && v.longitude) {
          m.setLatLng([v.latitude, v.longitude]);
          m.setIcon(createTruckIcon(v));
        }
      });
    } catch (e) {
      console.warn('Fleet map poll failed:', e);
    }
  }, 3000);

  setTimeout(() => map?.invalidateSize(), 100);

  return () => {
    window.removeEventListener('themechange', onThemeChange);
    destroyFleetMap();
  };
}

export function destroyFleetMap() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  clearRouteOverlay();
  if (map) {
    map.remove();
    map = null;
  }
  tileLayer = null;
  markers.clear();
  panelOpen = false;
}
